import { useState } from 'react';
import { FileText, Sparkles, ChevronRight, ChevronLeft, Plus, Trash2, Eye, EyeOff, X, Check, Coins, Upload, ZoomIn, ZoomOut, Search } from 'lucide-react';

interface DemoStep {
  number: number;
  title: string;
}

const steps: DemoStep[] = [
  { number: 1, title: 'Template' },
  { number: 2, title: 'Personal' },
  { number: 3, title: 'Experience' },
  { number: 4, title: 'Education' },
  { number: 5, title: 'Skills' },
];

const janeDoeDemoData = {
  personal: {
    fullName: 'Jane Doe',
    email: 'jane.doe@email.com',
    phone: '(555) 123-4567',
    location: 'Detroit, MI',
  },
  summary: 'Visionary tech entrepreneur and CEO with 10+ years of progressive experience spanning customer operations, financial services, and SaaS product development. Proven track record of building and scaling AI-powered platforms from concept to multi-million dollar revenue, securing venture funding, and leading high-performing teams to deliver exceptional business outcomes.',
  experience: [
    {
      company: 'TechVentures LLC',
      title: 'Founder & CEO',
      location: 'Detroit, MI',
      dates: 'Sep 2025 - Present',
      bullets: [
        'Founded and scaled AI-powered SaaS platform to 50,000+ users and $2.5M ARR within 12 months, achieving 140% net revenue retention',
        'Secured $5M Series A funding from top-tier VCs by demonstrating 300% YoY growth and compelling product-market fit',
        'Built and led cross-functional team of 25+ engineers, designers, and product managers using agile methodologies and OKR framework',
        'Architected scalable microservices infrastructure on AWS, processing 10M+ daily API requests with 99.99% uptime',
        'Established strategic partnerships with Fortune 500 companies, driving $1.8M in enterprise contracts and 60% MRR growth',
      ],
    },
    {
      company: 'Doe Community Credit Union',
      title: 'Call Center Supervisor',
      location: 'Detroit, MI',
      dates: 'May 2017 - Sep 2024',
      bullets: [
        'Supervised team of 15+ call center representatives, conducting performance evaluations and coaching sessions to maintain 95% quality assurance scores',
        'Developed and implemented comprehensive training programs for new hires, reducing onboarding time by 30% and improving first-call resolution rates',
        'Managed daily operations including scheduling, workflow optimization, and escalation handling, resulting in 25% improvement in customer satisfaction metrics',
        'Analyzed call center metrics and generated reports for senior management, identifying trends and recommending process improvements that increased efficiency by 20%',
        'Led cross-functional initiatives with IT and compliance teams to streamline customer service protocols and ensure regulatory adherence',
      ],
    },
    {
      company: 'Premier Financial Services',
      title: 'Team Lead - Customer Support',
      location: 'Detroit, MI',
      dates: 'Jan 2015 - Apr 2017',
      bullets: [
        'Promoted to Team Lead within 8 months, overseeing daily operations of 10-member customer support team handling 200+ inquiries daily',
        'Implemented quality monitoring system that improved team performance scores from 82% to 94% within six months',
        'Coordinated with training department to develop skill-building workshops, resulting in 40% reduction in escalated calls',
        'Managed scheduling and resource allocation to ensure optimal coverage during peak hours, maintaining service level agreements of 90% or higher',
        'Recognized as Employee of the Quarter twice for exceptional leadership and contributions to departmental goals',
      ],
    },
  ],
  education: [
    {
      school: 'Doe State University',
      degree: 'Bachelor of Science in Technology, Information Systems and Analytics (TISA)',
      location: 'Detroit, MI',
      graduationDate: '2010 - 2014',
    },
    {
      school: 'Amazing School of Arts',
      degree: 'Diploma',
      location: 'Detroit, MI',
      graduationDate: '2006 - 2010',
    },
  ],
  skills: [
    'Microsoft Office Suite',
    'Salesforce',
    'Customer Service',
    'Time Management',
    'Process Optimization',
    'Team Leadership',
    'Data Analysis',
    'Strategic Planning',
  ],
};

interface ToastProps {
  type: 'ai-built' | 'uploaded';
  onClose: () => void;
  onSignup: () => void;
}

function SuccessToast({ type, onClose, onSignup }: ToastProps) {
  const isAIBuilt = type === 'ai-built';

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`bg-white rounded-2xl shadow-2xl border-2 ${isAIBuilt ? 'border-orange-300' : 'border-green-300'} p-6 max-w-md`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isAIBuilt ? 'bg-orange-500' : 'bg-green-500'}`}>
            {isAIBuilt ? <Sparkles className="w-6 h-6 text-white" /> : <Check className="w-6 h-6 text-white" />}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-lg mb-2">
              {isAIBuilt ? 'Resume Generated Successfully!' : 'Resume Uploaded Successfully!'}
            </h3>
            <div className={`${isAIBuilt ? 'bg-orange-50' : 'bg-green-50'} rounded-xl p-4 mb-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">Credits {isAIBuilt ? 'Used' : 'Earned'}</span>
                <div className="flex items-center gap-2">
                  <Coins className={`w-5 h-5 ${isAIBuilt ? 'text-orange-600' : 'text-green-600'}`} />
                  <span className={`text-2xl font-bold ${isAIBuilt ? 'text-orange-600' : 'text-green-600'}`}>
                    {isAIBuilt ? '-10' : '+10'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {isAIBuilt
                  ? 'AI-powered resume generation with ATS optimization'
                  : 'Earned credits equivalent to AI resume building cost'}
              </p>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {isAIBuilt
                ? 'Your resume has been optimized for Applicant Tracking Systems and enhanced with AI-powered content.'
                : 'Your resume has been uploaded. You can now use AI to enhance it with premium features!'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onSignup}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-md"
              >
                Sign Up to Save
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ResumePreviewPane({ data }: { data: any; template: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-8 h-full">
      <div className="max-w-[8.5in] mx-auto bg-white" style={{ height: '11in' }}>
        {/* Header */}
        <div className="border-b-2 border-gray-300 pb-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{data.personal.fullName || 'Your Name'}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            {data.personal.email && <span>{data.personal.email}</span>}
            {data.personal.phone && <span>{data.personal.phone}</span>}
            {data.personal.location && <span>{data.personal.location}</span>}
          </div>
        </div>

        {/* Summary */}
        {data.summary && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2 border-b border-gray-300 pb-1">Professional Summary</h2>
            <p className="text-sm text-gray-700 leading-relaxed">{data.summary}</p>
          </div>
        )}

        {/* Experience */}
        {data.experience && data.experience.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Work Experience</h2>
            {data.experience.map((exp: any, idx: number) => (
              <div key={idx} className="mb-4">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="font-bold text-gray-900">{exp.title || 'Position Title'}</h3>
                    <p className="text-sm text-gray-700">{exp.company || 'Company Name'}</p>
                  </div>
                  <span className="text-sm text-gray-600">{exp.dates || 'Dates'}</span>
                </div>
                {exp.bullets && exp.bullets.length > 0 && (
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                    {exp.bullets.map((bullet: string, bulletIdx: number) => (
                      <li key={bulletIdx}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {data.education && data.education.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Education</h2>
            {data.education.map((edu: any, idx: number) => (
              <div key={idx} className="mb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-900">{edu.degree || 'Degree'}</h3>
                    <p className="text-sm text-gray-700">{edu.school || 'School Name'}</p>
                  </div>
                  <span className="text-sm text-gray-600">{edu.graduationDate || 'Year'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Skills */}
        {data.skills && data.skills.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {data.skills.map((skill: string, idx: number) => (
                <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md text-sm font-medium">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ResumeBuilderDemo() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState('professional');
  const [showPreview, setShowPreview] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<'ai-built' | 'uploaded'>('ai-built');
  const [generatingAI, setGeneratingAI] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0.55);

  const [demoData, setDemoData] = useState(janeDoeDemoData);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 1.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.3));
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleGenerateAIDescription = (expIndex: number) => {
    setGeneratingAI(expIndex);

    setTimeout(() => {
      const aiDescriptions = [
        [
          'Spearheaded cross-functional initiative resulting in 35% improvement in operational efficiency and customer satisfaction',
          'Orchestrated strategic partnerships with key stakeholders, driving $500K+ in revenue growth within first quarter',
          'Implemented data-driven decision-making framework that reduced costs by 25% while maintaining service quality',
          'Led team of 12+ professionals to exceed quarterly targets by 40% through innovative process optimization',
        ],
        [
          'Drove revenue growth of 45% year-over-year through strategic client relationship management and upselling',
          'Engineered automation solutions that increased team productivity by 60% and reduced error rates by 80%',
          'Championed customer-centric initiatives resulting in 95% satisfaction rating and 30% increase in retention',
          'Collaborated with C-suite executives to develop and execute company-wide digital transformation strategy',
        ],
      ];

      const newExperience = [...demoData.experience];
      const randomDescriptions = aiDescriptions[Math.floor(Math.random() * aiDescriptions.length)];
      newExperience[expIndex].bullets = randomDescriptions;

      setDemoData({
        ...demoData,
        experience: newExperience,
      });

      setGeneratingAI(null);
    }, 1500);
  };

  const handleAddExperience = () => {
    setDemoData({
      ...demoData,
      experience: [
        ...demoData.experience,
        {
          company: '',
          title: '',
          location: '',
          dates: '',
          bullets: [''],
        },
      ],
    });
  };

  const handleRemoveExperience = (index: number) => {
    setDemoData({
      ...demoData,
      experience: demoData.experience.filter((_, i) => i !== index),
    });
  };

  const handleUpdateExperience = (index: number, field: string, value: any) => {
    const newExperience = [...demoData.experience];
    (newExperience[index] as any)[field] = value;
    setDemoData({
      ...demoData,
      experience: newExperience,
    });
  };

  const handleGenerateResume = () => {
    setToastType('ai-built');
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 8000);
  };

  const handleUploadResume = () => {
    setToastType('uploaded');
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 8000);
  };

  const handleSignup = () => {
    window.location.href = '/signup';
  };

  return (
    <>
      {showToast && (
        <SuccessToast
          type={toastType}
          onClose={() => setShowToast(false)}
          onSignup={handleSignup}
        />
      )}

      <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-200 overflow-hidden max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Resume Builder Demo</h3>
                <p className="text-xs text-white/80">See how easy it is to build your resume</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg hover:bg-white/30 transition"
              >
                {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="text-sm font-semibold hidden sm:inline">Preview</span>
              </button>
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-lg">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-semibold">AI-Powered</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`flex items-center gap-2 ${
                  currentStep === step.number
                    ? 'text-orange-600'
                    : currentStep > step.number
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep === step.number
                      ? 'bg-orange-500 text-white'
                      : currentStep > step.number
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200'
                  }`}
                >
                  {step.number}
                </div>
                <span className="text-xs font-semibold hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`grid ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'} gap-6 p-6`}>
          {/* Form Section */}
          <div className="overflow-y-auto" style={{ maxHeight: '650px' }}>
            {currentStep === 1 && (
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Choose Your Template</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div
                    onClick={() => setSelectedTemplate('professional')}
                    className={`border-2 ${selectedTemplate === 'professional' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'} rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg`}
                  >
                    <div className="aspect-[8.5/11] bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg mb-3 flex flex-col items-center justify-start pt-3 shadow-sm border border-gray-200">
                      <div className="w-10 h-10 bg-gray-300 rounded-full mb-2 flex items-center justify-center border-2 border-gray-400">
                        <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                      </div>
                      <div className="text-center px-3 w-full">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="h-2 bg-gray-300 rounded w-3/4 mx-auto"></div>
                          <div className="h-1.5 bg-gray-300 rounded w-1/2 mx-auto mb-2"></div>
                          <div className="h-1 bg-gray-200 rounded w-full mt-2"></div>
                          <div className="h-1 bg-gray-200 rounded w-5/6 mx-auto"></div>
                          <div className="h-1 bg-gray-200 rounded w-full mt-1"></div>
                          <div className="h-1 bg-gray-200 rounded w-4/5 mx-auto"></div>
                        </div>
                      </div>
                    </div>
                    <h5 className="font-bold text-gray-900 text-sm">Professional</h5>
                    <p className="text-xs text-gray-600">Clean and modern</p>
                  </div>
                  <div
                    onClick={() => setSelectedTemplate('creative')}
                    className={`border-2 ${selectedTemplate === 'creative' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'} rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg`}
                  >
                    <div className="aspect-[8.5/11] bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg mb-3 flex flex-col items-center justify-start pt-3 shadow-sm border border-orange-200">
                      <div className="w-10 h-10 bg-orange-200 rounded-full mb-2 flex items-center justify-center border-2 border-orange-400">
                        <div className="w-4 h-4 bg-orange-400 rounded-full"></div>
                      </div>
                      <div className="text-center px-3 w-full">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="h-2 bg-orange-300 rounded w-3/4 mx-auto"></div>
                          <div className="h-1.5 bg-orange-200 rounded w-1/2 mx-auto mb-2"></div>
                          <div className="h-1 bg-gray-200 rounded w-full mt-2"></div>
                          <div className="h-1 bg-gray-200 rounded w-5/6 mx-auto"></div>
                          <div className="h-1 bg-gray-200 rounded w-full mt-1"></div>
                          <div className="h-1 bg-gray-200 rounded w-4/5 mx-auto"></div>
                        </div>
                      </div>
                    </div>
                    <h5 className="font-bold text-gray-900 text-sm">Creative</h5>
                    <p className="text-xs text-gray-600">Stand out design</p>
                  </div>
                </div>

                {/* Upload Option */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-2">Already have a resume?</h5>
                      <p className="text-sm text-gray-700 mb-3">
                        Upload your existing resume and earn <span className="font-bold text-green-600">+10 credits</span> — the same value as building one from scratch!
                      </p>
                      <button
                        onClick={handleUploadResume}
                        className="w-full px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Upload Resume & Earn Credits
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={demoData.personal.fullName}
                      onChange={(e) => setDemoData({ ...demoData, personal: { ...demoData.personal, fullName: e.target.value } })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={demoData.personal.email}
                        onChange={(e) => setDemoData({ ...demoData, personal: { ...demoData.personal, email: e.target.value } })}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={demoData.personal.phone}
                        onChange={(e) => setDemoData({ ...demoData, personal: { ...demoData.personal, phone: e.target.value } })}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm focus:border-orange-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={demoData.personal.location}
                      onChange={(e) => setDemoData({ ...demoData, personal: { ...demoData.personal, location: e.target.value } })}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Work Experience</h4>
                <div className="space-y-4">
                  {demoData.experience.map((exp, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between mb-3">
                        <h5 className="font-bold text-gray-900 text-sm">Experience {idx + 1}</h5>
                        {demoData.experience.length > 1 && (
                          <button
                            onClick={() => handleRemoveExperience(idx)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Job Title"
                          value={exp.title}
                          onChange={(e) => handleUpdateExperience(idx, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          placeholder="Company"
                          value={exp.company}
                          onChange={(e) => handleUpdateExperience(idx, 'company', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          placeholder="Dates (e.g., Jan 2020 - Present)"
                          value={exp.dates}
                          onChange={(e) => handleUpdateExperience(idx, 'dates', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                        />

                        <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700">Key Achievements</span>
                            <button
                              onClick={() => handleGenerateAIDescription(idx)}
                              disabled={generatingAI === idx}
                              className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-md text-xs font-semibold hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50"
                            >
                              {generatingAI === idx ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  <span>Generate with AI</span>
                                </>
                              )}
                            </button>
                          </div>
                          <ul className="space-y-1 text-xs text-gray-700">
                            {exp.bullets.slice(0, 3).map((bullet, bulletIdx) => (
                              <li key={bulletIdx} className="flex items-start gap-2">
                                <span className="text-orange-500 mt-0.5">•</span>
                                <span>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={handleAddExperience}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-600 font-semibold transition text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Experience
                  </button>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Education</h4>
                <div className="space-y-4">
                  {demoData.education.map((edu, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-900 text-sm">{edu.degree}</h5>
                          <p className="text-xs text-gray-600">{edu.school}</p>
                          <p className="text-xs text-gray-500 mt-1">{edu.location}</p>
                        </div>
                        <span className="text-xs text-gray-500 font-medium">{edu.graduationDate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Skills & Summary</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Key Skills</label>
                    <div className="flex flex-wrap gap-2">
                      {demoData.skills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-xs font-semibold"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-orange-600" />
                      <h5 className="font-bold text-gray-900 text-sm">AI-Enhanced Summary</h5>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {demoData.summary}
                    </p>
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Coins className="w-8 h-8 text-orange-600" />
                        <div>
                          <h5 className="font-bold text-gray-900">Ready to Generate?</h5>
                          <p className="text-xs text-gray-600">AI-powered resume with ATS optimization</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-orange-600">-10</div>
                        <div className="text-xs text-gray-600">credits</div>
                      </div>
                    </div>
                    <button
                      onClick={handleGenerateResume}
                      className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-amber-600 transition shadow-lg flex items-center justify-center gap-2"
                    >
                      <Sparkles className="w-5 h-5" />
                      Generate My Resume
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview Section - Widened */}
          {showPreview && (
            <div className="hidden lg:block">
              <div className="sticky top-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Live Preview
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.3}
                      className="p-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Zoom Out"
                    >
                      <ZoomOut className="w-4 h-4 text-gray-700" />
                    </button>
                    <span className="text-xs font-semibold text-gray-600 min-w-[3rem] text-center">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 1.0}
                      className="p-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Zoom In"
                    >
                      <ZoomIn className="w-4 h-4 text-gray-700" />
                    </button>
                  </div>
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden relative">
                    {/* Magnifying Glass Overlay */}
                    <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md">
                      <Search className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="transform origin-top-left" style={{
                      transform: `scale(${zoomLevel})`,
                      width: `${100 / zoomLevel}%`,
                      height: `${900 * (0.55 / zoomLevel)}px`
                    }}>
                      <ResumePreviewPane data={demoData} template={selectedTemplate} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {currentStep < 5 ? (
            <button
              onClick={nextStep}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition text-sm"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleGenerateResume}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-amber-600 transition shadow-lg text-sm"
            >
              <Sparkles className="w-4 h-4" />
              Generate Resume
            </button>
          )}
        </div>
      </div>
    </>
  );
}
